import React, { useEffect, useState } from 'react';
import { formatWon } from '../utils/number';

interface CostBasis {
 total_daily_cost: number;
 breakdown: Array<{
  child_family_hash: string;
  child_name: string;
  price_per_slot_per_day: number;
  slot_count: number;
  subtotal: number;
 }>;
}

interface Props {
 moFamilyHash: string | null | undefined;
 /** 현재 사용자가 입력한 단가 (원/회). 비교용. */
 pricePerImpression: number;
 /** 일 송출 추정 횟수 — number.ts 의 priceTooltipText 와 같은 1,920 기본값. */
 dailyImpressions?: number;
}

const DEFAULT_DAILY_IMPRESSIONS = 1920;

/**
 * 마켓 등록·제안 단가 입력 옆 권장 최저 단가 힌트.
 * 부모 패밀리의 incoming 비용 (모든 하위 관계 합산) 을 일 송출 추정치로 나눠
 * "권장 최저 단가" 산출. 사용자가 입력한 단가가 그 미만이면 노란 경고 박스.
 */
const PriceCostBasisHint: React.FC<Props> = ({ moFamilyHash, pricePerImpression, dailyImpressions }) => {
 const [costBasis, setCostBasis] = useState<CostBasis | null>(null);

 useEffect(() => {
  if (!moFamilyHash) {
   setCostBasis(null);
   return;
  }
  fetch(`/mofamily/${moFamilyHash}/cost-basis`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (j.success) setCostBasis(j.data);
   })
   .catch(() => setCostBasis(null));
 }, [moFamilyHash]);

 if (!costBasis || costBasis.total_daily_cost <= 0) return null;

 const impressions = dailyImpressions || DEFAULT_DAILY_IMPRESSIONS;
 const minPricePerImpression = costBasis.total_daily_cost / impressions;
 const underpriced = pricePerImpression > 0 && pricePerImpression < minPricePerImpression;

 return (
  <div className={`text-xs p-2 border ${underpriced ? 'bg-yellow-50 border-yellow-300 text-yellow-800' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
   <div className="flex items-start gap-2">
    <span>{underpriced ? '⚠️' : '📊'}</span>
    <div className="flex-1 min-w-0">
     <div>
      <strong>하위 패밀리 비용 합계:</strong> {formatWon(costBasis.total_daily_cost)}/일
     </div>
     <div>
      권장 최저 단가: 약 <strong>{minPricePerImpression.toFixed(2)}원/회</strong>
      {' '}<span className="text-[10px] opacity-70">({impressions.toLocaleString()}회/일 기준)</span>
     </div>
     {underpriced && (
      <div className="mt-1 font-medium">
       현재 단가는 비용을 회수하지 못합니다 — 적자 계약이 될 수 있습니다.
      </div>
     )}
     <details className="mt-1 opacity-80">
      <summary className="cursor-pointer text-[10px]">하위 관계 내역 보기</summary>
      <ul className="mt-1 space-y-0.5 pl-3">
       {costBasis.breakdown.map(b => (
        <li key={b.child_family_hash} className="text-[11px]">
         {b.child_name}: {formatWon(b.price_per_slot_per_day)}/슬롯 × {b.slot_count}개
         {' = '}{formatWon(b.subtotal)}/일
        </li>
       ))}
      </ul>
     </details>
    </div>
   </div>
  </div>
 );
};

export default PriceCostBasisHint;
